/**
 * RulePreviewModal Component
 *
 * Modal for previewing which transactions match a classification rule
 * and applying the rule to those transactions.
 *
 * Features:
 * - Shows matching transactions with current classification
 * - "Only pending classification" checkbox (default: checked)
 * - Current classification shown in red if different from target
 * - Apply button to update all matching transactions
 */

import { useState } from 'react';
import clsx from 'clsx';
import {
  X,
  AlertCircle,
  Check,
  Loader2,
  Target,
  Filter,
} from 'lucide-react';
import type { RulePreviewTransaction, RulePreviewRule } from '@/types/accounting';
import { AmountDisplay } from '@/pages/Accounting/shared/AmountDisplay';

// =============================================================================
// Types
// =============================================================================

export interface RulePreviewModalProps {
  /** The classification rule being previewed */
  rule: RulePreviewRule;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Callback when the apply button is clicked */
  onApply: (transactionIds: number[], includeClassified: boolean) => Promise<void>;
  /** Initial transactions to display */
  transactions: RulePreviewTransaction[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when filter changes */
  onFilterChange?: (onlyPending: boolean) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function truncateDescription(description: string, maxLength = 50): string {
  if (description.length <= maxLength) {
    return description;
  }
  return description.slice(0, maxLength) + '...';
}

// =============================================================================
// Sub-components
// =============================================================================

interface TransactionRowProps {
  transaction: RulePreviewTransaction;
  targetAccountPath: string;
}

function TransactionRow({ transaction, targetAccountPath }: TransactionRowProps) {
  const currentPath = transaction.currentAccountPath || 'Pending Classification';

  // Determine if classification should be shown in red
  // Red if: NOT pending AND current path is different from target
  const showInRed = !transaction.isPending &&
    transaction.currentAccountPath !== null &&
    transaction.currentAccountPath !== targetAccountPath;

  return (
    <tr className="hover:bg-gray-50">
      {/* Date */}
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
        {formatDate(transaction.date)}
      </td>

      {/* Description */}
      <td className="px-4 py-3 text-sm text-gray-900">
        <span
          className="font-mono text-xs block truncate max-w-[300px]"
          title={transaction.description}
        >
          {truncateDescription(transaction.description)}
        </span>
      </td>

      {/* Amount */}
      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
        <AmountDisplay
          amountCents={transaction.amountCents}
          currency="EUR"
          isIncome={transaction.direction === 'incoming'}
        />
      </td>

      {/* Current Classification */}
      <td className="px-4 py-3 text-sm">
        <span
          className={clsx(
            'inline-flex items-center gap-1.5',
            showInRed ? 'text-red-600 font-medium' : 'text-gray-500'
          )}
          title={showInRed ? 'Will be reclassified to target account' : undefined}
        >
          {showInRed && <AlertCircle size={14} className="text-red-500" />}
          <span className="truncate max-w-[200px]">{currentPath}</span>
        </span>
      </td>
    </tr>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function RulePreviewModal({
  rule,
  isOpen,
  onClose,
  onApply,
  transactions,
  isLoading = false,
  onFilterChange,
}: RulePreviewModalProps) {
  const [onlyPending, setOnlyPending] = useState(true);
  const [isApplying, setIsApplying] = useState(false);

  const handleFilterChange = (checked: boolean) => {
    setOnlyPending(checked);
    onFilterChange?.(checked);
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const transactionIds = transactions.map((t) => t.id);
      await onApply(transactionIds, !onlyPending);
      onClose();
    } finally {
      setIsApplying(false);
    }
  };

  // Count transactions that will be reclassified (not pending)
  const reclassifyCount = transactions.filter(
    (t) => !t.isPending && t.currentAccountPath !== rule.targetAccountPath
  ).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview Rule: {rule.name}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Review matching transactions before applying the rule
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            >
              <X size={20} />
            </button>
          </div>

          {/* Rule Info */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Pattern:</span>
                <code className="px-2 py-1 bg-gray-900 text-green-400 rounded text-xs font-mono">
                  /{rule.pattern}/i
                </code>
              </div>
              <div className="flex items-center gap-2">
                <Target size={14} className="text-gray-400" />
                <span className="text-gray-500">Target:</span>
                <span className="font-medium text-gray-900">
                  {rule.targetAccountPath}
                </span>
              </div>
            </div>
          </div>

          {/* Filter */}
          <div className="px-6 py-3 border-b border-gray-200 bg-white">
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyPending}
                onChange={(e) => handleFilterChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  Only pending classification
                </span>
              </div>
            </label>
            {!onlyPending && reclassifyCount > 0 && (
              <p className="mt-2 text-sm text-amber-600 flex items-center gap-1.5">
                <AlertCircle size={14} />
                {reclassifyCount} transaction{reclassifyCount !== 1 ? 's' : ''} will
                be reclassified from their current accounts
              </p>
            )}
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-blue-600" />
                <span className="ml-3 text-gray-500">Loading transactions...</span>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-2">
                  <Check size={48} className="mx-auto" />
                </div>
                <p className="text-gray-600 font-medium">No matching transactions</p>
                <p className="text-gray-500 text-sm mt-1">
                  {onlyPending
                    ? 'All matching transactions are already classified'
                    : 'No transactions match this pattern'}
                </p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Classification
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {transactions.map((transaction) => (
                      <TransactionRow
                        key={transaction.id}
                        transaction={transaction}
                        targetAccountPath={rule.targetAccountPath}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-500">
              {transactions.length > 0 && (
                <span>
                  {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} will
                  be classified as{' '}
                  <span className="font-medium text-gray-700">{rule.targetAccountPath}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'bg-white text-gray-700 border border-gray-300',
                  'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
                )}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={isApplying || transactions.length === 0}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'bg-blue-600 text-white',
                  'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'inline-flex items-center gap-2'
                )}
              >
                {isApplying ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Apply to {transactions.length} Transaction{transactions.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RulePreviewModal;
